import { X } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import { OVERLAY_SOFT } from "@/components/ui/overlay";

interface HowCalculatedSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface StepCardProps {
  step: number;
  title: string;
  children: React.ReactNode;
}

function StepCard({ step, title, children }: StepCardProps) {
  return (
    <div className="bg-(--color-surface-muted) rounded-lg p-4 flex flex-col gap-2 shrink-0">
      <div className="flex items-center gap-2">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-(--color-surface-chip-active) text-xs font-normal text-(--color-text-on-dark)">
          {step}
        </span>
        <span className="text-lg font-medium text-(--color-text-primary)">{title}</span>
      </div>
      <div className="text-base text-(--color-text-primary)/60 leading-snug">
        {children}
      </div>
    </div>
  );
}

export function HowCalculatedSheet({ open, onOpenChange }: HowCalculatedSheetProps) {
  const { t } = useLingui();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right-inset"
        overlayClassName={OVERLAY_SOFT}
        hideClose
        className="w-156"
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-(--color-border) shrink-0">
          <div className="flex flex-col gap-2">
            <SheetTitle className="text-2xl font-medium text-(--color-text-primary)">
              {t`How risk and efficiency are calculated`}
            </SheetTitle>
            <SheetDescription className="text-base text-(--color-text-primary)/40">
              {t`Capacity is estimated from queued work, scheduled cook hours, and measured efficiency.`}
            </SheetDescription>
          </div>
          <SheetClose className="shrink-0 text-(--color-text-primary)/40 hover:text-(--color-text-primary) transition-colors mt-0.5">
            <X className="h-5 w-5" />
            <span className="sr-only">{t`Close`}</span>
          </SheetClose>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-3">
          <StepCard step={1} title={t`Work required`}>
            {t`Sum of target minutes for all queued prep items (On deck). If "Include cooking now" is enabled, we also include the remaining target minutes for in-progress items.`}
          </StepCard>

          <StepCard step={2} title={t`Capacity available`}>
            {t`Scheduled cooks × scheduled hours × 60. Then we adjust by measured efficiency.`}
          </StepCard>

          <StepCard step={3} title={t`Measured efficiency`}>
            {t`From items "Cooking now": efficiency = average(target ÷ elapsed). If cooks are running slower than target, efficiency drops (e.g., 0.85). If faster, it rises (capped). Converted to a 0–100 score for quick scanning.`}
          </StepCard>

          <StepCard step={4} title={t`Risk levels`}>
            <ul className="flex flex-col gap-1 mt-1">
              <li className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full bg-(--color-success) shrink-0" />
                {t`On track: utilization < 85%`}
              </li>
              <li className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full bg-(--color-warning) shrink-0" />
                {t`At risk: 85%–100%`}
              </li>
              <li className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full bg-(--color-danger) shrink-0" />
                {t`Over capacity: > 100%`}
              </li>
            </ul>
          </StepCard>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-5 border-t border-(--color-border) shrink-0">
          <SheetClose asChild>
            <Button
              variant="default"
              className="h-13 w-30 rounded-[0.625rem] text-base"
            >
              {t`Got it`}
            </Button>
          </SheetClose>
        </div>
      </SheetContent>
    </Sheet>
  );
}
